import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * Searchable single-select dropdown:
 * long client/staff lists get a type-to-filter box instead of a native
 * <select>. Styling mirrors the standard form input classes.
 *
 * Props:
 *   value        — the selected option value ('' when empty)
 *   onChange     — (newValue) => void ('' on clear)
 *   options      — [{ value, label, group? }]; when any option carries a
 *                  `group`, the list renders under section headers (the
 *                  staff pickers use the designation), in the groups'
 *                  first-appearance order. Typing matches the label OR the
 *                  group name, so "manager" lists the whole section.
 *   placeholder  — input placeholder (default 'Select…')
 *   disabled
 */
const SearchSelect = ({ value, onChange, options, placeholder = 'Select…', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(q) || (o.group && o.group.toLowerCase().includes(q)));
  }, [options, query]);

  // [{ group, items }] when grouped, null when every option is ungrouped.
  const sections = useMemo(() => {
    if (!filtered.some((o) => o.group)) return null;
    const order = [];
    const byGroup = new Map();
    filtered.forEach((o) => {
      const g = o.group || 'Others';
      if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
      byGroup.get(g).push(o);
    });
    return order.map((g) => ({ group: g, items: byGroup.get(g) }));
  }, [filtered]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (opt) => {
    onChange(String(opt.value));
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={boxRef} className="relative">
      <div
        className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1 bg-white ${disabled ? 'bg-slate-50 text-slate-400' : 'cursor-text focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'}`}
        onClick={() => { if (!disabled) setOpen(true); }}
      >
        <input
          type="text"
          className="flex-1 outline-none bg-transparent min-w-0"
          value={open ? query : (selected ? selected.label : '')}
          placeholder={selected ? selected.label : placeholder}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); setQuery(''); }
            if (e.key === 'Enter') {
              e.preventDefault();
              if (open && filtered.length > 0) pick(filtered[0]);
            }
          }}
        />
        {selected && !disabled && (
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600"
            onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); }}
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      </div>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
          )}
          {(sections || [{ group: null, items: filtered }]).map((sec) => (
            <React.Fragment key={sec.group || '__flat__'}>
              {sec.group && (
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {sec.group}
                </div>
              )}
              {sec.items.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(o.value) === String(value) ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}`}
                  onClick={() => pick(o)}
                >
                  {o.label}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
